import { useEffect, useLayoutEffect } from 'react'

import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

/**
 * The original site is a Framer build, so its interaction transitions all run
 * on one curve: cubic-bezier(0.44, 0, 0.56, 1). Keeping a single shared set of
 * constants is what makes separate sections read as one choreography rather
 * than a pile of unrelated fade-ins.
 */
export const FRAMER_EASE = 'cubic-bezier(0.44, 0, 0.56, 1)'

export const EASE = {
    /** Entrance decelerate, used for every reveal. */
    enter: 'power3.out',
    /** Symmetric curve for open / close state changes. */
    state: 'power2.inOut'
} as const

export const MOTION = {
    /** Word / element entrance. */
    duration: 0.9,
    /** Distance an entering element travels upward, in pixels. */
    distance: 24,
    /** Blur an entering element resolves from, in pixels. */
    blur: 10,
    /** Delay between consecutive items in a stagger. */
    stagger: 0.055,
    /** Viewport position a section starts animating at. */
    start: 'top 85%'
} as const

let registered = false

export const registerGsap = (): typeof gsap => {
    if (!registered && typeof window !== 'undefined') {
        gsap.registerPlugin(ScrollTrigger)
        gsap.config({ nullTargetWarn: false })
        registered = true
    }

    return gsap
}

/**
 * useLayoutEffect on the client, useEffect on the server. Running the setup
 * before paint is what prevents a flash of un-animated content, and the branch
 * never changes between renders so it cannot cause a hydration mismatch.
 */
export const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect

export const prefersReducedMotion = (): boolean => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return false
    }

    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export { gsap, ScrollTrigger }
